import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    institution: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        institution: formData.institution || null,
      });
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-slate-50 py-12">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white p-8 md:p-12 rounded-sm shadow-lg border border-slate-100">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <img src="/logo-new.png" alt="UIEHEC" className="h-10 w-auto" />
            </Link>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B]">
              Create Account
            </h1>
            <p className="text-slate-600 mt-2">
              Register to access member features
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="full_name" className="text-[#0E285B]">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                placeholder="John Doe"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0E285B]">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                placeholder="your@email.com"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="institution" className="text-[#0E285B]">
                Institution (Optional)
              </Label>
              <Input
                id="institution"
                name="institution"
                value={formData.institution}
                onChange={handleChange}
                className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                placeholder="Your institution name"
                data-testid="register-institution-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0E285B]">
                Password <span className="text-red-500">*</span>
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                placeholder="••••••••"
                data-testid="register-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#0E285B]">
                Confirm Password <span className="text-red-500">*</span>
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                placeholder="••••••••"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#0E285B] text-white hover:bg-[#0E285B]/90 h-11"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm">
            <p className="text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#F5A623] font-medium hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
